import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CalendarDays, Check, ChevronDown, Columns3, MessageSquare } from 'lucide-react';
import PageLayout from '../../components/layouts/PageLayout';
import AnalyticsExportMenu, {
  AnalyticsExportFormat,
} from '../../components/analytics/AnalyticsExportMenu';
import { authService } from '../../auth/authService';
import { canAccessPage } from '../../utils/permissions';
import { getDefaultRoute } from '../../utils/getDefaultRoute';
import {
  getNotificationDeliveryAnalytics,
  NotificationDeliveryAnalyticsData,
  NotificationDeliveryChannelRow,
  NotificationDeliveryFilterOption,
  NotificationDeliveryTrendPoint,
} from '../../services/notificationDeliveryAnalyticsService';
import { exportNotificationDeliveryAnalytics } from '../../utils/notificationDeliveryAnalyticsExport';
import '../../components/analytics/analytics-theme.css';
import './NotificationDelivery.css';

type DeliveryStatus = 'delivered' | 'failed' | 'pending';
type ColumnKey = keyof NotificationDeliveryChannelRow;
type Granularity = 'day' | 'week' | 'month';

const GRANULARITY_X_LABEL: Record<Granularity, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
};

const MAX_RANGE_DAYS = 90;

const TABLE_COLUMNS: ReadonlyArray<{ key: ColumnKey; label: string }> = [
  { key: 'channel', label: 'Channel' },
  { key: 'total', label: 'Total sent' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'failed', label: 'Failed' },
  { key: 'pending', label: 'Pending' },
  { key: 'averageTime', label: 'Avg delivery time' },
];

const DEFAULT_FILTER_OPTIONS = {
  eventCategories: [
    { value: '', label: 'All event categories' },
    { value: 'report-ready', label: 'Report ready' },
    { value: 'appointment-confirmation', label: 'Appointment confirmation' },
    { value: 'no-show-alert', label: 'No-show alert' },
    { value: 'no-show-revert', label: 'No-show revert' },
    { value: 'referring-physician', label: 'Referring physician' },
    { value: 'radiologist-otp', label: 'Radiologist OTP' },
    { value: 'critical-result', label: 'Critical result alert' },
  ],
  channels: [
    { value: '', label: 'All channels' },
    { value: 'SMS', label: 'SMS' },
    { value: 'WHATSAPP', label: 'WhatsApp' },
  ],
  statuses: [
    { value: '', label: 'All statuses' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'failed', label: 'Failed' },
    { value: 'pending', label: 'Pending' },
  ],
};

const EMPTY_DATA: NotificationDeliveryAnalyticsData = {
  summary: { totalSent: 0, deliverySuccessRate: 0, pendingNotifications: 0 },
  trendPoints: [],
  channelRows: [],
  filterOptions: DEFAULT_FILTER_OPTIONS,
  meta: { warnings: [] },
};

function renderNotificationTrendSection(
  loading: boolean,
  trendPoints: NotificationDeliveryTrendPoint[],
  status: DeliveryStatus,
  chartYMax: number,
  granularity: Granularity
): React.ReactNode {
  if (loading) {
    return <div className="notification-empty-chart">Loading chart…</div>;
  }
  if (trendPoints.length === 0) {
    return <div className="notification-empty-chart">No data for the selected date range.</div>;
  }
  return (
    <NotificationTrendChart
      points={trendPoints}
      status={status}
      yMax={chartYMax}
      granularity={granularity}
    />
  );
}

function renderChannelTableBody(
  loading: boolean,
  channelRows: NotificationDeliveryChannelRow[],
  activeColumns: ReadonlyArray<{ key: ColumnKey; label: string }>
): React.ReactNode {
  if (loading) {
    return (
      <tr>
        <td colSpan={activeColumns.length}>Loading…</td>
      </tr>
    );
  }
  if (channelRows.length === 0) {
    return (
      <tr>
        <td colSpan={activeColumns.length}>No data for the selected filters.</td>
      </tr>
    );
  }
  return channelRows.map(row => (
    <tr key={row.channel}>
      {activeColumns.map(column => (
        <td key={column.key}>{row[column.key]}</td>
      ))}
    </tr>
  ));
}

const CHART_WIDTH = 1000;
const CHART_HEIGHT = 350;
const PADDING = { top: 18, right: 18, bottom: 52, left: 52 };

function todayIsoDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function thirtyDaysAgoIsoDate(): string {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatShortDate(value: string): string {
  const [, month, day] = value.split('-');
  return `${month}-${day}`;
}

function formatFilterDate(value: string): string {
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
}

function addDaysIso(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function spanDaysInclusive(start: string, end: string): number {
  return (
    (Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / (24 * 60 * 60 * 1000) + 1
  );
}

function mergeFilterOptions(
  remote?: NotificationDeliveryAnalyticsData['filterOptions'] | null
): NotificationDeliveryAnalyticsData['filterOptions'] {
  const pick = (
    remoteList: NotificationDeliveryFilterOption[] | undefined,
    fallback: NotificationDeliveryFilterOption[]
  ) => (Array.isArray(remoteList) && remoteList.length > 0 ? remoteList : fallback);

  return {
    eventCategories: pick(remote?.eventCategories, DEFAULT_FILTER_OPTIONS.eventCategories),
    channels: pick(remote?.channels, DEFAULT_FILTER_OPTIONS.channels),
    statuses: pick(remote?.statuses, DEFAULT_FILTER_OPTIONS.statuses),
  };
}

function computeChartYMax(points: NotificationDeliveryTrendPoint[]): number {
  const maxValue = points.reduce(
    (max, point) => Math.max(max, point.delivered, point.failed, point.pending),
    0
  );
  if (maxValue <= 4) {
    return 4;
  }
  if (maxValue <= 8) {
    return 8;
  }
  if (maxValue <= 12) {
    return 12;
  }
  if (maxValue <= 16) {
    return 16;
  }
  return Math.ceil(maxValue / 4) * 4;
}

function linePath(
  points: NotificationDeliveryTrendPoint[],
  key: DeliveryStatus,
  yMax: number
): string {
  if (!points.length) {
    return '';
  }
  const innerWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  const innerHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  const coordinates = points.map((point, index) => {
    const x =
      PADDING.left +
      (points.length === 1 ? innerWidth / 2 : (index / (points.length - 1)) * innerWidth);
    const y = PADDING.top + innerHeight - (point[key] / yMax) * innerHeight;
    return { x, y };
  });
  if (coordinates.length === 1) {
    return `M ${coordinates[0].x} ${coordinates[0].y}`;
  }

  let path = `M ${coordinates[0].x.toFixed(2)} ${coordinates[0].y.toFixed(2)}`;
  for (let index = 0; index < coordinates.length - 1; index += 1) {
    const previous = coordinates[Math.max(0, index - 1)];
    const current = coordinates[index];
    const next = coordinates[index + 1];
    const afterNext = coordinates[Math.min(coordinates.length - 1, index + 2)];
    const controlOneX = current.x + (next.x - previous.x) / 6;
    const controlOneY = current.y + (next.y - previous.y) / 6;
    const controlTwoX = next.x - (afterNext.x - current.x) / 6;
    const controlTwoY = next.y - (afterNext.y - current.y) / 6;
    path += ` C ${controlOneX.toFixed(2)} ${controlOneY.toFixed(2)}, ${controlTwoX.toFixed(2)} ${controlTwoY.toFixed(2)}, ${next.x.toFixed(2)} ${next.y.toFixed(2)}`;
  }
  return path;
}

function areaPath(points: NotificationDeliveryTrendPoint[], yMax: number): string {
  if (!points.length) {
    return '';
  }
  const innerWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  const bottom = CHART_HEIGHT - PADDING.bottom;
  const lastX = PADDING.left + innerWidth;
  return `${linePath(points, 'delivered', yMax)} L ${lastX} ${bottom} L ${PADDING.left} ${bottom} Z`;
}

function labelForOption(
  options: NotificationDeliveryFilterOption[],
  value: string,
  fallback: string
): string {
  if (!value) {
    return fallback;
  }
  return options.find(option => option.value === value)?.label || value;
}

function SelectFilter({
  label,
  value,
  options,
  onChange,
}: Readonly<{
  label: string;
  value: string;
  options: NotificationDeliveryFilterOption[];
  onChange: (value: string) => void;
}>) {
  const id = `notification-${label.toLowerCase().replaceAll(/\s+/g, '-')}`;
  return (
    <div className="notification-filter-field">
      <label htmlFor={id}>{label}</label>
      <div className="notification-select-wrap">
        <select
          id={id}
          value={value}
          onChange={event => onChange(event.target.value)}
        >
          {options.map(option => (
            <option
              key={option.value || 'all'}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={15}
          aria-hidden
        />
      </div>
    </div>
  );
}

function DateFilter({
  label,
  value,
  min,
  max,
  onChange,
}: Readonly<{
  label: string;
  value: string;
  min?: string;
  max?: string;
  onChange: (value: string) => void;
}>) {
  const id = `notification-${label.toLowerCase()}`;

  return (
    <div className="notification-filter-field">
      <label htmlFor={id}>{label}</label>
      <div className="notification-date-wrap">
        <span aria-hidden="true">{formatFilterDate(value)}</span>
        <CalendarDays
          size={16}
          aria-hidden
        />
        <input
          id={id}
          type="date"
          value={value}
          min={min}
          max={max}
          onChange={event => onChange(event.target.value)}
          aria-label={label}
        />
      </div>
    </div>
  );
}

function NotificationTrendChart({
  points,
  status,
  yMax,
  granularity,
}: Readonly<{
  points: NotificationDeliveryTrendPoint[];
  status: string;
  yMax: number;
  granularity: Granularity;
}>) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const visibleStatuses: DeliveryStatus[] = status
    ? [status as DeliveryStatus]
    : ['delivered', 'failed', 'pending'];
  const colors: Record<DeliveryStatus, string> = {
    delivered: '#16a34a',
    failed: '#ef4444',
    pending: '#0891b2',
  };
  const labels: Record<DeliveryStatus, string> = {
    delivered: 'Delivered',
    failed: 'Failed',
    pending: 'Pending',
  };
  const yTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let tick = 0; tick <= yMax; tick += Math.max(4, yMax / 4)) {
      ticks.push(tick);
    }
    if (ticks.at(-1) !== yMax) {
      ticks.push(yMax);
    }
    return [...new Set(ticks)].sort((a, b) => a - b);
  }, [yMax]);
  const innerWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  const innerHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  const xForIndex = (index: number) =>
    PADDING.left +
    (points.length === 1 ? innerWidth / 2 : (index / (points.length - 1)) * innerWidth);
  const yForValue = (value: number) => PADDING.top + innerHeight - (value / yMax) * innerHeight;
  const activePoint = hoveredIndex === null ? null : points[hoveredIndex];
  const activeX = hoveredIndex === null ? 0 : xForIndex(hoveredIndex);
  const activeY = activePoint
    ? Math.min(...visibleStatuses.map(series => yForValue(activePoint[series])))
    : 0;

  const handleMouseMove = (event: React.MouseEvent<SVGRectElement>) => {
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg || points.length === 0) {
      return;
    }
    const screenMatrix = svg.getScreenCTM();
    if (!screenMatrix) {
      return;
    }
    const pointer = svg.createSVGPoint();
    pointer.x = event.clientX;
    pointer.y = event.clientY;
    const pointerX = pointer.matrixTransform(screenMatrix.inverse()).x;
    const rawIndex = ((pointerX - PADDING.left) / innerWidth) * (points.length - 1);
    setHoveredIndex(Math.max(0, Math.min(points.length - 1, Math.round(rawIndex))));
  };

  return (
    <div className="notification-chart-wrap">
      <svg
        className="notification-chart"
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        aria-labelledby="notification-delivery-trend-chart-title"
      >
        <title id="notification-delivery-trend-chart-title">Notification delivery trend</title>
        <defs>
          <linearGradient
            id="notification-delivered-area"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop
              offset="0%"
              stopColor="#16a34a"
              stopOpacity="0.22"
            />
            <stop
              offset="100%"
              stopColor="#16a34a"
              stopOpacity="0.03"
            />
          </linearGradient>
        </defs>
        {yTicks.map(tick => {
          const y = PADDING.top + innerHeight - (tick / yMax) * innerHeight;
          return (
            <g key={tick}>
              <line
                x1={PADDING.left}
                y1={y}
                x2={CHART_WIDTH - PADDING.right}
                y2={y}
              />
              <text
                x={PADDING.left - 8}
                y={y + 4}
                textAnchor="end"
              >
                {tick}
              </text>
            </g>
          );
        })}
        <line
          className="notification-axis"
          x1={PADDING.left}
          y1={PADDING.top}
          x2={PADDING.left}
          y2={CHART_HEIGHT - PADDING.bottom}
        />
        <line
          className="notification-axis"
          x1={PADDING.left}
          y1={CHART_HEIGHT - PADDING.bottom}
          x2={CHART_WIDTH - PADDING.right}
          y2={CHART_HEIGHT - PADDING.bottom}
        />
        <text
          className="notification-axis-title"
          x={15}
          y={CHART_HEIGHT / 2}
          textAnchor="middle"
          transform={`rotate(-90 15 ${CHART_HEIGHT / 2})`}
        >
          Notifications
        </text>
        {points.map((point, index) => {
          if (index % 2 !== 0 && index !== points.length - 1) {
            return null;
          }
          const x = xForIndex(index);
          return (
            <text
              key={point.date}
              className="notification-date-label"
              x={x}
              y={CHART_HEIGHT - 33}
              textAnchor="middle"
            >
              {formatShortDate(point.date)}
            </text>
          );
        })}
        {visibleStatuses.includes('delivered') && (
          <path
            className="notification-area"
            d={areaPath(points, yMax)}
          />
        )}
        {visibleStatuses.map(series => (
          <path
            key={series}
            className="notification-series"
            d={linePath(points, series, yMax)}
            stroke={colors[series]}
          />
        ))}
        {activePoint
          ? visibleStatuses.map(series => (
              <circle
                key={series}
                className="notification-hover-dot"
                cx={activeX}
                cy={yForValue(activePoint[series])}
                r={4}
                fill={colors[series]}
              />
            ))
          : null}
        <rect
          className="notification-chart-overlay"
          x={PADDING.left}
          y={PADDING.top}
          width={innerWidth}
          height={innerHeight}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoveredIndex(null)}
        />
        <text
          className="notification-axis-title notification-day-label"
          x={CHART_WIDTH / 2}
          y={CHART_HEIGHT - 7}
          textAnchor="middle"
        >
          {GRANULARITY_X_LABEL[granularity]}
        </text>
      </svg>
      {activePoint ? (
        <div
          className="notification-chart-tooltip"
          style={{
            left: `${(activeX / CHART_WIDTH) * 100}%`,
            top: `${(activeY / CHART_HEIGHT) * 100}%`,
            transform:
              activeX > CHART_WIDTH * 0.78
                ? 'translate(calc(-100% - 12px), 10px)'
                : 'translate(12px, 10px)',
          }}
        >
          <strong>{formatShortDate(activePoint.date)}</strong>
          {visibleStatuses.map(series => (
            <span key={series}>
              <i style={{ backgroundColor: colors[series] }} />
              {labels[series]}
              <b>{activePoint[series]}</b>
            </span>
          ))}
        </div>
      ) : null}
      <div className="notification-chart-legend">
        {visibleStatuses.map(series => (
          <span key={series}>
            <i style={{ backgroundColor: colors[series] }} />
            {labels[series]}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function NotificationDelivery() {
  const [fromDate, setFromDate] = useState(thirtyDaysAgoIsoDate);
  const [toDate, setToDate] = useState(todayIsoDate);
  const [eventCategory, setEventCategory] = useState('');
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(
    TABLE_COLUMNS.map(column => column.key)
  );
  const [data, setData] = useState<NotificationDeliveryAnalyticsData>(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const columnsMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (columnsMenuRef.current && !columnsMenuRef.current.contains(event.target as Node)) {
        setColumnsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (spanDaysInclusive(fromDate, toDate) > MAX_RANGE_DAYS) {
      setError(`Date range must not exceed ${MAX_RANGE_DAYS} days`);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const timer = globalThis.setTimeout(() => {
      setLoading(true);
      setError(null);
      getNotificationDeliveryAnalytics({
        startDate: fromDate,
        endDate: toDate,
        eventCategory: eventCategory || undefined,
        channel: channel || undefined,
        status: status || undefined,
        granularity,
      })
        .then(result => {
          if (cancelled) {
            return;
          }
          if (result.success && result.data) {
            setData({
              ...result.data,
              filterOptions: mergeFilterOptions(result.data.filterOptions),
            });
          } else {
            setData(EMPTY_DATA);
            setError(result.error || 'Failed to load notification delivery analytics');
          }
        })
        .catch(err => {
          if (cancelled) {
            return;
          }
          setData(EMPTY_DATA);
          setError(
            err instanceof Error ? err.message : 'Failed to load notification delivery analytics'
          );
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });
    }, 300);

    return () => {
      cancelled = true;
      globalThis.clearTimeout(timer);
    };
  }, [fromDate, toDate, eventCategory, channel, status, granularity]);

  const filterOptions = mergeFilterOptions(data.filterOptions);
  const summary = data.summary;
  const trendPoints = data.trendPoints;
  const channelRows = data.channelRows;
  const chartYMax = computeChartYMax(trendPoints);
  const activeColumns = TABLE_COLUMNS.filter(column => visibleColumns.includes(column.key));
  const successRate = summary.deliverySuccessRate.toFixed(1);
  const user = authService.getUser();

  if (!canAccessPage(user, '/analytics/notification-delivery')) {
    return (
      <Navigate
        to={getDefaultRoute(user)}
        replace
      />
    );
  }

  const handleExport = (format: AnalyticsExportFormat) => {
    exportNotificationDeliveryAnalytics(
      format,
      data,
      {
        startDate: fromDate,
        endDate: toDate,
        eventCategoryLabel: labelForOption(
          filterOptions.eventCategories,
          eventCategory,
          'All event categories'
        ),
        channelLabel: labelForOption(filterOptions.channels, channel, 'All channels'),
        statusLabel: labelForOption(filterOptions.statuses, status, 'All statuses'),
        granularityLabel: granularity.charAt(0).toUpperCase() + granularity.slice(1),
      },
      visibleColumns
    );
  };

  const toggleColumn = (key: ColumnKey) => {
    setVisibleColumns(current =>
      current.includes(key) ? current.filter(column => column !== key) : [...current, key]
    );
  };

  return (
    <PageLayout
      title="Notification delivery"
      hideTitleInHeader
      headerVariant="light"
      contentClassName="notification-page-layout-content"
      breadcrumbs={[
        { label: 'Dashboard', path: '/admin-dashboard' },
        { label: 'Admin' },
        { label: 'Analytics' },
        { label: 'Notification delivery' },
      ]}
    >
      <main className="notification-dashboard">
        <header className="notification-page-heading">
          <div>
            <h1>Notification delivery</h1>
            <p>Track delivery performance across all notification channels.</p>
          </div>
        </header>

        <section
          className="notification-filters"
          aria-label="Notification delivery filters"
        >
          <DateFilter
            label="From"
            value={fromDate}
            min={addDaysIso(toDate, -(MAX_RANGE_DAYS - 1))}
            max={toDate}
            onChange={setFromDate}
          />
          <DateFilter
            label="To"
            value={toDate}
            min={fromDate}
            max={todayIsoDate()}
            onChange={value => {
              setToDate(value);
              if (spanDaysInclusive(fromDate, value) > MAX_RANGE_DAYS) {
                setFromDate(addDaysIso(value, -(MAX_RANGE_DAYS - 1)));
              }
            }}
          />
          <SelectFilter
            label="Event category"
            value={eventCategory}
            options={filterOptions.eventCategories}
            onChange={setEventCategory}
          />
          <SelectFilter
            label="Channel"
            value={channel}
            options={filterOptions.channels}
            onChange={setChannel}
          />
          <SelectFilter
            label="Delivery status"
            value={status}
            options={filterOptions.statuses}
            onChange={setStatus}
          />
        </section>

        {error ? (
          <p
            className="notification-error"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        {error === null && data.meta?.warnings?.length ? (
          <output className="notification-warning">
            {data.meta.warnings.map(warning => (
              <p key={warning}>{warning}</p>
            ))}
          </output>
        ) : null}

        <section
          className="notification-kpis"
          aria-label="Notification summary"
        >
          <article>
            <p>Total notifications sent</p>
            <strong>{loading ? '—' : summary.totalSent}</strong>
          </article>
          <article>
            <p>Delivery success rate</p>
            <strong>{loading ? '—' : `${successRate}%`}</strong>
          </article>
          <article>
            <p>Pending notifications</p>
            <strong>{loading ? '—' : summary.pendingNotifications}</strong>
          </article>
        </section>

        <section className="notification-chart-card">
          <div className="notification-card-heading">
            <div>
              <MessageSquare
                size={16}
                aria-hidden
              />
              <h2>Notification delivery trend</h2>
            </div>
            <div className="notification-period-select">
              <select
                aria-label="Chart interval"
                value={granularity}
                onChange={event => setGranularity(event.target.value as 'day' | 'week' | 'month')}
              >
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
              <ChevronDown
                size={14}
                aria-hidden
              />
            </div>
          </div>
          {renderNotificationTrendSection(loading, trendPoints, status, chartYMax, granularity)}
        </section>

        <section className="notification-table-section">
          <div className="notification-table-heading">
            <h2>Delivery by channel</h2>
            <div className="notification-table-actions">
              <div
                className="notification-columns-wrap"
                ref={columnsMenuRef}
              >
                <button
                  type="button"
                  onClick={() => setColumnsOpen(open => !open)}
                >
                  <Columns3 size={16} />
                  Columns
                  <ChevronDown size={14} />
                </button>
                {columnsOpen ? (
                  <div className="notification-columns-menu">
                    {TABLE_COLUMNS.map(column => {
                      const checked = visibleColumns.includes(column.key);
                      return (
                        <button
                          key={column.key}
                          type="button"
                          className={checked ? 'selected' : ''}
                          onClick={() => toggleColumn(column.key)}
                        >
                          <span>{column.label}</span>
                          {checked ? <Check size={15} /> : null}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              <AnalyticsExportMenu
                onExport={handleExport}
                disabled={loading || !activeColumns.length || channelRows.length === 0}
              />
            </div>
          </div>
          <div className="notification-table-wrap">
            <table>
              <thead>
                <tr>
                  {activeColumns.map(column => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>{renderChannelTableBody(loading, channelRows, activeColumns)}</tbody>
            </table>
          </div>
        </section>
      </main>
    </PageLayout>
  );
}
